import React, { useEffect } from "react";
import Layout from "../components/Layout";
import { HeadFC, PageProps, Script, graphql } from "gatsby";
import { IGatsbyImageData } from "gatsby-plugin-image";
import { BlogHero } from "../components/BlogHero";
import GridContainer from "@upsun/react-component-library/src/components/GridContainer";
import GridCol from "@upsun/react-component-library/src/components/GridCol";
import { Author } from "../components/Author";
import SocialMediaIcons from "../components/SocialMediaIcons";
import { css } from "@emotion/react";
import { pxToRem } from "@upsun/react-component-library/src/styles/css-utils";
import { parse } from "node-html-parser";
import Prism from "prismjs";
import BlogBody from "../components/blog/BlogBody";
import { SEO } from "../components/Seo";
import CallToAction from "@upsun/react-component-library/src/components/CallToAction";

const BlogPost: React.FC<PageProps<Queries.BlogPostQueryQuery>> = ({
  data,
  location,
}: PageProps<Queries.BlogPostQueryQuery>) => {
  const blogPost = data?.strapiBlogPost;
  const strapiImages = blogPost?.images;
  let blogPostBody: string & TrustedHTML = blogPost?.body?.data?.body || "";

  const root = parse(blogPostBody, {
    blockTextElements: {
      pre: true,
      code: true,
      script: false,
      noscript: false,
    },
  });
  const images = root.getElementsByTagName("img");

  //Remove all inline styles
  root.querySelectorAll("[style]").forEach((el) => el.removeAttribute("style"));
  root.querySelectorAll("script, noscript").forEach((el) => el.remove());

  images?.forEach((img) => {
    const fileName = img.getAttribute("src");
    const strapiImage = strapiImages?.find((img) => {
      return (
        img?.url?.split("/")[img?.url?.split("/").length - 1] ===
        fileName?.split("/")[fileName?.split("/").length - 1]
      );
    });
    img.removeAttribute("width");
    img.removeAttribute("height");
    const imgSrc = strapiImage?.childImageSharp
      ? strapiImage?.childImageSharp?.gatsbyImageData.images.fallback?.src
      : strapiImage?.publicURL;
    img.setAttribute("src", imgSrc as string);
  });

  blogPostBody = root.innerHTML;

  const coverImage = blogPost?.cover_image?.localFile?.childImageSharp
    ?.gatsbyImageData as IGatsbyImageData;

  const tags = blogPost?.tags?.map((tag) => {
    return tag?.name;
  }) as string[];

  useEffect(() => {
    Prism.highlightAll();
  }, []);

  // @todo check w/ design about this hex code; it matches the mockup but not any color in the design system palette
  const horizontalRule = css`
    width: 100%;
    height: ${pxToRem(2)};
    background-color: #313c42;
    margin-bottom: ${pxToRem(24)};
  `;

  let SEOFAQjson = null;

  if (data.strapiBlogPost?.QA?.length) {
    const mainEntities = data.strapiBlogPost?.QA?.map((qa) => ({
      "@type": "Question",
      name: qa?.Question,
      acceptedAnswer: {
        "@type": "Answer",
        text: qa?.Answer,
      },
    }));
    const completedPost = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: mainEntities,
    };
    SEOFAQjson = JSON.stringify(completedPost);
  }

  let authorSchema = {
    "@type": "Organization",
    name: "Upsun",
    logo: "https://upsun.com/logos/logo_horizontal_black.svg",
    description: "A highly flexible PaaS",
  };

  if (data.strapiBlogPost?.authors?.length) {
    const authors = data.strapiBlogPost?.authors?.map((author) => ({
      "@type": "Person",
      name: author?.name || "",
      image: "https://upsun.com" + author?.image?.localFile?.publicURL,
      description: author?.job_title || "",
      logo: "https://upsun.com/logos/logo_horizontal_black.svg",
    }));

    authorSchema = authors[0];
  }

  let SEOBlogjson = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: data.strapiBlogPost?.title,
    url: "https://upsun.com" + location.pathname,
    description: data.strapiBlogPost?.meta_description,
    datePublished: data.strapiBlogPost?.date_override || data.strapiBlogPost?.publishedAt,
    dateModified: data.strapiBlogPost?.updatedAt,
    image:
      "https://upsun.com" +
      data.strapiBlogPost?.cover_image?.localFile?.publicURL,
    author: authorSchema,
    publisher: {
      "@type": "Organization",
      name: "Upsun",
      url: "https://upsun.com",
      logo: "https://upsun.com/logos/logo_horizontal_black.svg",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://upsun.com" + location.pathname,
    },
  };

  let blogSchema = JSON.stringify(SEOBlogjson);

  const defaultCTAProps = {
    text: "Your greatest work",
    highlightedText: "is just on the horizon",
    btnHref: "https://auth.upsun.com/register/",
    btnText: "Free trial"
  };

  const callToActionProps = data?.strapiBlogPost?.CallToAction ? {
    text: data?.strapiBlogPost?.CallToAction?.Title || defaultCTAProps.text,
    subtitle: data?.strapiBlogPost?.CallToAction?.subtitle?.data?.subtitle || undefined,
    btnHref: data?.strapiBlogPost?.CallToAction?.ButtonLink || defaultCTAProps.btnHref,
    btnText: data?.strapiBlogPost?.CallToAction?.ButtonText || defaultCTAProps.btnText,
    btn2Href: data?.strapiBlogPost?.CallToAction?.SecondButtonLink || undefined,
    btn2Text: data?.strapiBlogPost?.CallToAction?.SecondButtonText || undefined,
  } : defaultCTAProps;

  return (
    <Layout cta={callToActionProps}>
      {SEOFAQjson && <Script type="application/ld+json">{SEOFAQjson}</Script>}
      <Script type="application/ld+json">{blogSchema}</Script>
      <BlogHero
        title={blogPost?.title || ""}
        date={blogPost?.date_override || blogPost?.publishedAt || ""}
        tags={tags}
        image={coverImage}
        altText={""}
        backLinkHref={"/blog"}
        backLinkTxt={"Blog"}
      />
      <GridContainer>
        <GridCol colStart={1} colEnd={13} colStartMd={2} colEndMd={12} colEndLg={4}>
          {blogPost?.authors?.map((author) => (
            <Author
              key={author?.name}
              name={author?.name || ""}
              title={author?.job_title || ""}
              company={author?.company || ""}
              image={
                author?.image?.localFile?.childImageSharp
                  ?.gatsbyImageData as IGatsbyImageData
              }
            />
          ))}
          <SocialMediaIcons
            pathname={location.pathname}
            siteUrl={data?.site?.siteMetadata?.siteUrl || ""}
          />
        </GridCol>
        <GridCol colStart={1} colEnd={13} colStartMd={2} colEndMd={12} colStartLg={4} colEndLg={10}>
          <div css={horizontalRule} />
          <BlogBody body={blogPostBody} />
        </GridCol>
      </GridContainer>
    </Layout>
  );
};

export default BlogPost;

export const query = graphql`
  query BlogPostQuery($id: String!) {
    strapiBlogPost(id: { eq: $id }) {
      id
      meta_title
      meta_description
      images {
        url
        childImageSharp {
          gatsbyImageData(formats: WEBP)
        }
        publicURL
      }
      body {
        data {
          body
        }
      }
      cover_image {
        localFile {
          childImageSharp {
            gatsbyImageData(quality: 90)
          }
          publicURL
        }
      }
      social_image {
        localFile {
          publicURL
        }
      }
      title
      url
      tags {
        name
      }
      date_override(formatString: "DD MMMM YYYY")
      publishedAt(formatString: "DD MMMM YYYY")
      updatedAt(formatString: "DD MMMM YYYY")
      authors {
        name
        job_title
        company
        image {
          url
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(quality: 90)
            }
          }
        }
      }
      QA {
        Answer
        Question
      }
      CallToAction {
        Title
        subtitle {
          data {
            subtitle
          }
        }
        ButtonLink
        ButtonText
        SecondButtonLink
        SecondButtonText
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;

export const Head: HeadFC<Queries.BlogPostQueryQuery> = ({ data, location }) => (
  <SEO
    title={data.strapiBlogPost?.meta_title || data.strapiBlogPost?.title || ""}
    description={data.strapiBlogPost?.meta_description || ""}
    pathname={location.pathname}
    socialImage={data.strapiBlogPost?.social_image?.localFile?.publicURL || ""}
    noFollow={data.strapiBlogPost?.publishedAt === null}
  />
);
